import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { CardsSyncPanel } from "../components/payments/CardsSyncPanel";
import { DuesAttendancePanel } from "../components/payments/DuesAttendancePanel";
import { BillingGroupsPanel } from "../components/billing/BillingGroupsPanel";
import { ContractsWithoutSubPanel } from "../components/billing/ContractsWithoutSubPanel";
import { PausedSubsPanel } from "../components/billing/PausedSubsPanel";
import { RelanceTrackingPanel } from "../components/billing/RelanceTrackingPanel";
import { StripeHealthPanel } from "../components/billing/StripeHealthPanel";
import { AuditPanel } from "../components/billing/AuditPanel";
import { OutboxPanel } from "../components/billing/OutboxPanel";

const STATUS_BADGE = {
  a_jour: { label: "À jour", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  en_retard: { label: "En retard", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const fmt = (n) => `${Number(n || 0).toFixed(2)} $`;

export default function MemberPaymentsModule() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "membres");
  const [followupBusy, setFollowupBusy] = useState(false);
  const [followupLog, setFollowupLog] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState({}); // member_email -> card status

  // Form: nouveau plan
  const [planForm, setPlanForm] = useState({
    name: "", discipline: "kickboxing", frequency: "illimite", age_group: "adultes",
    duration_weeks: 12, total_price: "", max_installments: 1,
  });
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Form: nouvel abonnement
  const [subForm, setSubForm] = useState({
    member_name: "", member_email: "", member_phone: "", plan_id: "", start_date: "",
    num_installments: 1, payment_method: "comptant", auto_renew: true,
  });
  // Validation entrée
  const [checkQ, setCheckQ] = useState("");
  const [checkRes, setCheckRes] = useState(null);

  // Réactivation d'un ancien membre (import ZenPlanner)
  const [formerOpen, setFormerOpen] = useState(false);
  const [formerList, setFormerList] = useState([]);
  const [formerQ, setFormerQ] = useState("");
  const [formerLoading, setFormerLoading] = useState(false);

  const loadFormer = useCallback(async (q = "") => {
    setFormerLoading(true);
    try {
      const r = await api.get(`/member-payments/former-members${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setFormerList(r.data.members || []);
    } catch { toast.error("Chargement des anciens membres impossible"); }
    setFormerLoading(false);
  }, []);

  const pickFormer = (m) => {
    setSubForm((s) => ({ ...s, member_name: m.full_name, member_email: m.email || "", member_phone: m.phone || "" }));
    setFormerOpen(false);
    toast.success(`${m.full_name} pré-rempli — choisis le plan et le mode de paiement`);
  };

  // Grille de tarifs « Kickboxing & Muay-Thaï »
  const [pricing, setPricing] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [newDuration, setNewDuration] = useState("");

  const loadPricing = useCallback(async () => {
    try {
      const r = await api.get("/member-payments/pricing/config");
      setPricing(r.data);
    } catch { toast.error("Chargement des tarifs impossible"); }
  }, []);
  useEffect(() => { if (tab === "tarifs" && !pricing) loadPricing(); }, [tab, pricing, loadPricing]);

  const setCell = (dur, freq, val) => {
    setPricing((p) => {
      const grid = { ...(p.grid || {}) };
      grid[dur] = { ...(grid[dur] || {}), [freq]: val === "" ? "" : Number(val) };
      return { ...p, grid };
    });
  };
  const addDuration = () => {
    const d = parseInt(newDuration, 10);
    if (!d || d <= 0) return;
    setPricing((p) => ({ ...p, durations: [...new Set([...p.durations, d])].sort((a, b) => a - b) }));
    setNewDuration("");
  };
  const removeDuration = (d) => setPricing((p) => ({ ...p, durations: p.durations.filter((x) => x !== d) }));

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const r = await api.put("/member-payments/pricing/config", {
        program_name: pricing.program_name,
        surcharge_pct: Number(pricing.surcharge_pct) || 0,
        durations: pricing.durations,
        grid: pricing.grid,
      });
      toast.success(`Tarifs enregistrés · ${r.data.synced_plans} plans synchronisés`);
      loadAll();
    } catch { toast.error("Échec de l'enregistrement des tarifs"); }
    setPricingSaving(false);
  };

  const downloadPricingSheet = async () => {
    try {
      const r = await api.get("/member-payments/pricing/sheet.pdf", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "tarifs-dojo-kaizen.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("📄 Fiche de tarifs (PDF) téléchargée");
    } catch { toast.error("Impossible de générer le PDF des tarifs"); }
  };

  const loadAll = useCallback(async () => {
    try {
      const [d, m, p] = await Promise.all([
        api.get("/member-payments/dashboard"),
        api.get("/member-payments/members"),
        api.get("/member-payments/plans"),
      ]);
      setDashboard(d.data);
      setMembers(m.data.members);
      setPlans(p.data.plans);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Retour de Stripe (?paid=session_id)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("paid");
    if (sess) {
      api.get(`/member-payments/checkout/status/${sess}`)
        .then((r) => {
          if (r.data.payment_status === "paid") toast.success("Paiement carte reçu ✓");
          loadAll();
        })
        .catch(() => {})
        .finally(() => window.history.replaceState({}, "", window.location.pathname));
    }
  }, [loadAll]);

  // Retour de Stripe (?card_session=) — finalise l'enregistrement de carte côté admin
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("card_session");
    if (sess) {
      api.get(`/member-payments/admin-setup-status/${sess}`)
        .then((r) => { if (r.data.status === "saved") toast.success(`Carte enregistrée ✓ (${r.data.member_email})`); })
        .catch(() => {})
        .finally(() => { window.history.replaceState({}, "", window.location.pathname); loadAll(); });
    } else if (params.get("card_cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadAll]);

  const loadCard = useCallback(async (email) => {
    try {
      const r = await api.get(`/member-payments/members/${encodeURIComponent(email)}/card`);
      setCards((c) => ({ ...c, [email]: r.data }));
    } catch { /* ignore */ }
  }, []);

  const toggleRow = (m) => {
    const open = expanded === m.subscription_id;
    setExpanded(open ? null : m.subscription_id);
    if (!open) loadCard(m.member_email);
  };

  const adminSetupCard = async (email) => {
    try {
      const r = await api.post(`/member-payments/members/${encodeURIComponent(email)}/admin-setup-session`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Erreur Stripe"); }
  };

  const removeCard = async (email) => {
    if (!window.confirm("Retirer la carte enregistrée de ce membre ?")) return;
    try {
      await api.delete(`/member-payments/members/${encodeURIComponent(email)}/card`);
      toast.success("Carte retirée");
      loadCard(email);
    } catch { toast.error("Erreur"); }
  };

  const chargeSavedCard = async (paymentId, email) => {
    try {
      const r = await api.post(`/member-payments/payments/${paymentId}/charge-saved-card`);
      if (r.data.ok) { toast.success(`Carte chargée ✓ · reçu ${r.data.receipt_no}`); loadAll(); }
      else toast.error(r.data.error || "Échec du paiement");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const createPlan = async () => {
    if (!planForm.name || !planForm.total_price) return toast.error("Nom et prix requis");
    const body = {
      ...planForm,
      duration_weeks: Number(planForm.duration_weeks),
      total_price: Number(planForm.total_price),
      max_installments: Number(planForm.max_installments),
    };
    try {
      if (editingPlanId) {
        await api.put(`/member-payments/plans/${editingPlanId}`, body);
        toast.success("Plan modifié");
      } else {
        await api.post("/member-payments/plans", body);
        toast.success("Plan créé");
      }
      cancelEditPlan();
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur enregistrement plan"); }
  };

  const editPlan = (p) => {
    setEditingPlanId(p.id);
    setPlanForm({
      name: p.name || "", discipline: p.discipline || "kickboxing", frequency: p.frequency || "illimite",
      age_group: p.age_group || "adultes", duration_weeks: p.duration_weeks ?? 12,
      total_price: p.total_price ?? "", max_installments: p.max_installments ?? 1,
    });
    const el = document.querySelector('[data-testid="plan-form"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const cancelEditPlan = () => {
    setEditingPlanId(null);
    setPlanForm({ name: "", discipline: "kickboxing", frequency: "illimite", age_group: "adultes", duration_weeks: 12, total_price: "", max_installments: 1 });
  };

  const duplicatePlan = async (p) => {
    try {
      await api.post(`/member-payments/plans/${p.id}/duplicate`);
      toast.success("Plan dupliqué");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur duplication"); }
  };

  const togglePlanActive = async (p) => {
    try {
      const { data } = await api.post(`/member-payments/plans/${p.id}/toggle-active`);
      toast.success(data.active ? "Plan réactivé" : "Plan archivé");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deletePlan = async (p) => {
    if (!window.confirm(`Supprimer le plan « ${p.name} » ?`)) return;
    try {
      await api.delete(`/member-payments/plans/${p.id}`);
      toast.success("Plan supprimé");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur suppression plan"); }
  };

  const createSub = async () => {
    if (!subForm.member_name || !subForm.member_email || !subForm.plan_id || !subForm.start_date)
      return toast.error("Tous les champs sont requis");
    try {
      await api.post("/member-payments/subscriptions", {
        ...subForm, num_installments: Number(subForm.num_installments),
      });
      toast.success("Abonnement créé + échéancier généré");
      setSubForm({ ...subForm, member_name: "", member_email: "", plan_id: "", start_date: "", num_installments: 1 });
      setTab("membres");
      loadAll();
    } catch (e) { toast.error("Erreur création abonnement"); }
  };

  const recordCash = async (paymentId) => {
    try {
      const r = await api.post(`/member-payments/payments/${paymentId}/record-cash`, { note: "Comptant" });
      toast.success(`Comptant enregistré · reçu ${r.data.receipt_no}`);
      loadAll();
    } catch (e) { toast.error("Erreur paiement comptant"); }
  };

  const payCard = async (paymentId) => {
    try {
      const r = await api.post(`/member-payments/payments/${paymentId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error("Erreur Stripe"); }
  };

  const sendReminder = async (paymentId) => {
    try {
      const r = await api.post(`/member-payments/payments/${paymentId}/send-reminder`);
      const res = r.data.result;
      toast.success(`Relance envoyée${res.email ? " · courriel ✓" : ""}${res.sms ? " · SMS ✓" : ""}`);
    } catch (e) { toast.error("Erreur envoi relance"); }
  };

  const runFollowups = async () => {
    setFollowupBusy(true);
    try {
      const { data } = await api.get("/contract-followups/preview");
      if (!data.candidates) { toast.success("Aucun membre en retard sans échéancier 🎉"); return; }
      const list = (data.preview || []).slice(0, 12)
        .map((x) => `• ${x.name}${x.amount ? ` — ${x.amount}` : ""} (${x.email})`).join("\n");
      const ok = window.confirm(
        `🔁 Relance des membres en retard (transition)\n\n` +
        `${data.candidates} membre(s) seront relancés par courriel (avec paiement Interac).\n\n` +
        `${list}${(data.preview || []).length > 12 ? `\n…+${data.preview.length - 12} autres` : ""}\n\n` +
        `Envoyer maintenant ? (max 1 courriel / 5 jours par membre)`
      );
      if (!ok) return;
      const res = await api.post("/contract-followups/run?dry_run=false");
      toast.success(`✅ ${res.data.sent} relance(s) envoyée(s) · ${res.data.skipped_cooldown} ignorée(s) (déjà relancés récemment)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur relances transition");
    } finally { setFollowupBusy(false); }
  };

  const loadFollowupLog = async () => {
    try {
      const { data } = await api.get("/contract-followups/log");
      setFollowupLog(data);
    } catch (e) { toast.error("Erreur chargement suivi relances"); }
  };

  const markPaid = async (contractId, name) => {
    if (!window.confirm(`Marquer « ${name} » comme réglé ? (retire le statut « en retard »)`)) return;
    try {
      await api.post(`/contract-followups/${contractId}/mark-paid`);
      toast.success("Marqué comme réglé ✓");
      loadFollowupLog();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const runCheckIn = async () => {
    if (!checkQ.trim()) return;
    try {
      const r = await api.get(`/member-payments/check-in?q=${encodeURIComponent(checkQ.trim())}`);
      setCheckRes(r.data);
    } catch (e) { toast.error("Erreur recherche"); }
  };

  const a = dashboard?.alerts || {};

  return (
    <div className="p-6 max-w-6xl mx-auto text-zinc-100" data-testid="member-payments-module">
      <h1 className="text-3xl font-bold mb-1">💳 Paiements Membres</h1>
      <p className="text-zinc-400 mb-4">Membres actifs · paiements faits · paiements à venir.</p>

      <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="followup-banner">
        <div>
          <div className="text-sm font-semibold text-amber-300">🔁 Relance des membres en retard (transition ZenPlanner)</div>
          <div className="text-xs text-zinc-400">Relance auto quotidienne par courriel (avec paiement Interac) les contrats « en retard » sans échéancier. Max 1 courriel / 5 jours.</div>
        </div>
        <button onClick={runFollowups} disabled={followupBusy} data-testid="run-followups-btn"
          className="shrink-0 px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50">
          {followupBusy ? "…" : "Prévisualiser / Relancer"}
        </button>
      </div>

      {/* Tableau de bord */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="alert-overdue">
          <div className="text-sm text-red-300">🔴 En retard</div>
          <div className="text-2xl font-bold">{fmt(a.overdue_amount)}</div>
          <div className="text-xs text-zinc-400">{a.overdue_count || 0} versement(s)</div>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="alert-due-soon">
          <div className="text-sm text-amber-300">🟠 Échéances 7 jours</div>
          <div className="text-2xl font-bold">{fmt(a.due_soon_amount)}</div>
          <div className="text-xs text-zinc-400">{a.due_soon_count || 0} versement(s)</div>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="alert-active">
          <div className="text-sm text-emerald-300">🟢 Abonnements actifs</div>
          <div className="text-2xl font-bold">{dashboard?.active_subscriptions || 0}</div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-5 border-b border-zinc-800">
        {[["membres", "Membres actifs"], ["cartes", "💳 Cartes Stripe"], ["regrouper", "⛓ Regrouper"], ["sante", "🩺 Santé Stripe"], ["approuver", "📨 À approuver"], ["defaut", "⚠️ Présents en défaut"], ["tarifs", "💲 Tarifs"], ["plans", "Plans"], ["nouveau", "Nouvel abonnement"], ["validation", "✅ Validation entrée"], ["relances", "🔁 Relances"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); if (k === "relances") loadFollowupLog(); }}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === k ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
            data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {loading && <p className="text-zinc-400">Chargement…</p>}

      {/* MEMBRES ACTIFS */}
      {!loading && tab === "membres" && (
        <div className="space-y-3" data-testid="members-list">
          <ContractsWithoutSubPanel />
          <RelanceTrackingPanel />
          <PausedSubsPanel />
          {members.length === 0 && <p className="text-zinc-500">Aucun abonnement actif. Crée-en un dans « Nouvel abonnement ».</p>}
          {members.map((m) => {
            const b = STATUS_BADGE[m.status] || STATUS_BADGE.a_jour;
            const open = expanded === m.subscription_id;
            return (
              <div key={m.subscription_id} className="rounded-xl border border-zinc-800 bg-zinc-900/50" data-testid={`member-row-${m.subscription_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer"
                  onClick={() => toggleRow(m)}>
                  <div>
                    <div className="font-semibold">{m.member_name}</div>
                    <div className="text-xs text-zinc-400">{m.member_email} · {m.plan_name}</div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full border text-xs ${b.cls}`}>{b.label}</span>
                    <span className="text-emerald-400">Payé {fmt(m.amount_paid)}</span>
                    <span className="text-red-400">Dû {fmt(m.amount_owed)}</span>
                    {m.next_due && <span className="text-zinc-400">Prochain : {m.next_due.date} ({fmt(m.next_due.amount)})</span>}
                  </div>
                </div>
                {open && (
                  <div className="border-t border-zinc-800 p-4 space-y-2" data-testid={`member-payments-${m.subscription_id}`}>
                    {/* Carte enregistrée */}
                    {(() => {
                      const cd = cards[m.member_email];
                      return (
                        <div className="flex flex-wrap items-center justify-between gap-2 bg-zinc-950/40 rounded-lg px-3 py-2 mb-1" data-testid={`card-block-${m.subscription_id}`}>
                          <span className="text-sm">
                            💳 {cd?.has_card
                              ? <span className="text-emerald-400">{String(cd.brand || "Carte").toUpperCase()} •••• {cd.last4} (exp {String(cd.exp_month).padStart(2, "0")}/{cd.exp_year}) · enregistrée par {cd.saved_by === "admin" ? "admin" : "le membre"}</span>
                              : <span className="text-zinc-400">Aucune carte enregistrée</span>}
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => adminSetupCard(m.member_email)} className="px-2 py-1 text-xs rounded bg-indigo-600 hover:bg-indigo-500" data-testid={`setup-card-${m.subscription_id}`}>
                              {cd?.has_card ? "Changer la carte" : "Enregistrer la carte"}
                            </button>
                            {cd?.has_card && (
                              <button onClick={() => removeCard(m.member_email)} className="px-2 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600" data-testid={`remove-card-${m.subscription_id}`}>Retirer</button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {m.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm bg-zinc-950/40 rounded-lg px-3 py-2">
                        <span>Versement {p.installment_index} · échéance {p.due_date} · {fmt(p.amount)}</span>
                        <div className="flex items-center gap-2">
                          {p.status === "paid" ? (
                            <span className="text-emerald-400 text-xs">✓ Payé ({p.method}{p.receipt_no ? ` · ${p.receipt_no}` : ""})</span>
                          ) : (
                            <>
                              <button onClick={() => recordCash(p.id)} className="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500" data-testid={`cash-${p.id}`}>Comptant</button>
                              {cards[m.member_email]?.has_card && (
                                <button onClick={() => chargeSavedCard(p.id, m.member_email)} className="px-2 py-1 text-xs rounded bg-violet-600 hover:bg-violet-500" data-testid={`charge-saved-${p.id}`}>Carte enr.</button>
                              )}
                              <button onClick={() => payCard(p.id)} className="px-2 py-1 text-xs rounded bg-indigo-600 hover:bg-indigo-500" data-testid={`card-${p.id}`}>Carte</button>
                              <button onClick={() => sendReminder(p.id)} className="px-2 py-1 text-xs rounded bg-amber-600 hover:bg-amber-500" data-testid={`remind-${p.id}`}>Relance</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* TARIFS — grille « Kickboxing & Muay-Thaï » */}
      {!loading && tab === "tarifs" && (
        <div data-testid="pricing-grid">
          {!pricing ? (
            <p className="text-zinc-500">Chargement des tarifs…</p>
          ) : (
            <div className="space-y-5 max-w-4xl">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90">
                Tu saisis les <b>prix comptant (réguliers)</b> par fréquence. L'app calcule automatiquement le prix
                <b> prélèvement (+{pricing.surcharge_pct}%)</b>, disponible dès <b>{pricing.installment_min_weeks} semaines</b>
                {" "}(1 prélèvement toutes les 4 semaines). L'âge/le niveau ne change pas le prix.
              </div>

              {/* Page publique des tarifs : lien + QR (à mettre sur affiches / page d'accueil) */}
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="tarifs-public-panel">
                <div className="bg-white p-2 rounded-lg shrink-0">
                  <QRCodeCanvas value={`${window.location.origin}/tarifs`} size={96} level="M" data-testid="tarifs-public-qr" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white">📣 Page publique « Voir nos tarifs »</div>
                  <p className="text-xs text-zinc-400 mt-1">
                    Partage ce lien (ou imprime le QR) sur tes affiches, ta vitrine et ta page d'accueil. Les prospects voient les prix 24/7, sans te texter.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <code className="text-xs bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-amber-300 break-all" data-testid="tarifs-public-url">{`${window.location.origin}/tarifs`}</code>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tarifs`); toast.success("Lien des tarifs copié"); }}
                      className="text-xs px-3 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" data-testid="tarifs-copy-link-btn">Copier le lien</button>
                    <a href="/tarifs" target="_blank" rel="noreferrer"
                      className="text-xs px-3 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" data-testid="tarifs-open-public-btn">Ouvrir la page</a>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-zinc-400">Nom du programme</span>
                  <input className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm mt-1"
                    value={pricing.program_name} onChange={(e) => setPricing({ ...pricing, program_name: e.target.value })}
                    data-testid="pricing-program-name" />
                </label>
                <label className="block">
                  <span className="text-xs text-zinc-400">Frais de prélèvement (%)</span>
                  <input type="number" min="0" step="0.5" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm mt-1"
                    value={pricing.surcharge_pct} onChange={(e) => setPricing({ ...pricing, surcharge_pct: e.target.value })}
                    data-testid="pricing-surcharge" />
                </label>
              </div>

              <div className="overflow-x-auto rounded-xl border border-zinc-800">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900 text-zinc-400 text-xs">
                    <tr>
                      <th className="text-left px-3 py-2">Durée</th>
                      {["1x", "2x", "3x"].map((f) => <th key={f} className="text-left px-3 py-2">{f} / semaine (comptant $)</th>)}
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {pricing.durations.map((dur) => {
                      const installAvail = dur >= pricing.installment_min_weeks;
                      const mult = 1 + (Number(pricing.surcharge_pct) || 0) / 100;
                      const nInst = Math.floor(dur / 4);
                      return (
                        <tr key={dur} data-testid={`pricing-row-${dur}`}>
                          <td className="px-3 py-3 font-semibold whitespace-nowrap">{dur} sem
                            <div className="text-[10px] font-normal text-zinc-500">{installAvail ? `prélèv. dispo (${nInst}×)` : "comptant seult"}</div>
                          </td>
                          {["1x", "2x", "3x"].map((f) => {
                            const v = (pricing.grid[dur] || {})[f];
                            const reg = Number(v) || 0;
                            return (
                              <td key={f} className="px-3 py-2">
                                <input type="number" min="0" step="1" placeholder="—"
                                  className="w-24 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5"
                                  value={v ?? ""} onChange={(e) => setCell(dur, f, e.target.value)}
                                  data-testid={`price-${dur}-${f}`} />
                                {installAvail && reg > 0 && (
                                  <div className="text-[10px] text-emerald-400/80 mt-1">
                                    prélèv: {(reg * mult).toFixed(2)}$ ({nInst}×{((reg * mult) / nInst).toFixed(2)}$)
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => removeDuration(dur)} className="text-zinc-500 hover:text-red-400" data-testid={`remove-duration-${dur}`} title="Retirer cette durée">×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <input type="number" min="1" placeholder="Ajouter une durée (sem)" value={newDuration}
                  onChange={(e) => setNewDuration(e.target.value)}
                  className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm w-52" data-testid="new-duration-input" />
                <button onClick={addDuration} className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-sm" data-testid="add-duration-btn">+ Ajouter la durée</button>
                <button onClick={downloadPricingSheet}
                  className="px-4 py-2.5 rounded bg-amber-600 hover:bg-amber-500 text-white font-semibold text-sm"
                  data-testid="download-pricing-pdf-btn">
                  📄 Tarifs imprimables (PDF)
                </button>
                <button onClick={savePricing} disabled={pricingSaving}
                  className="ml-auto px-5 py-2.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50"
                  data-testid="save-pricing-btn">
                  {pricingSaving ? "Enregistrement…" : "💾 Enregistrer les tarifs"}
                </button>
              </div>
              <p className="text-[11px] text-zinc-500">À l'enregistrement, chaque tarif devient un plan réutilisable dans « Nouvel abonnement ». En choisissant « Prélèvement bancaire », le prix +{pricing.surcharge_pct}% et les versements aux 4 semaines s'appliquent automatiquement.</p>
            </div>
          )}
        </div>
      )}

      {/* PLANS */}
      {!loading && tab === "plans" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 space-y-3" data-testid="plan-form">
            <h3 className="font-semibold">{editingPlanId ? "Modifier le plan" : "Nouveau plan"}</h3>
            <input className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Nom (ex. Kickboxing 12 sem 3x)" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name" />
            <div className="grid grid-cols-2 gap-2">
              <select className="bg-zinc-950 border border-zinc-700 rounded px-2 py-2 text-sm" value={planForm.discipline} onChange={(e) => setPlanForm({ ...planForm, discipline: e.target.value })} data-testid="plan-discipline">
                {["kickboxing", "muay_thai", "bjj", "mma", "self_defense"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select className="bg-zinc-950 border border-zinc-700 rounded px-2 py-2 text-sm" value={planForm.frequency} onChange={(e) => setPlanForm({ ...planForm, frequency: e.target.value })} data-testid="plan-frequency">
                {["1x", "2x", "3x", "illimite"].map((f) => <option key={f} value={f}>{f}/sem</option>)}
              </select>
              <select className="bg-zinc-950 border border-zinc-700 rounded px-2 py-2 text-sm" value={planForm.age_group} onChange={(e) => setPlanForm({ ...planForm, age_group: e.target.value })} data-testid="plan-age">
                {["enfants", "ados", "adultes", "famille"].map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <input type="number" className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Durée (sem)" value={planForm.duration_weeks} onChange={(e) => setPlanForm({ ...planForm, duration_weeks: e.target.value })} data-testid="plan-duration" />
              <input type="number" className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Prix total $" value={planForm.total_price} onChange={(e) => setPlanForm({ ...planForm, total_price: e.target.value })} data-testid="plan-price" />
              <input type="number" className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Max versements" value={planForm.max_installments} onChange={(e) => setPlanForm({ ...planForm, max_installments: e.target.value })} data-testid="plan-installments" />
            </div>
            <button onClick={createPlan} className="w-full py-2 rounded bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400" data-testid="plan-create-btn">{editingPlanId ? "💾 Enregistrer les modifications" : "Créer le plan"}</button>
            {editingPlanId && (
              <button onClick={cancelEditPlan} className="w-full py-2 rounded border border-zinc-700 text-zinc-300 font-semibold hover:bg-zinc-800" data-testid="plan-cancel-edit-btn">Annuler</button>
            )}
          </div>
          <div className="space-y-2" data-testid="plans-list">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer mb-1" data-testid="show-archived-label">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived-check" />
              Afficher les plans archivés {(() => { const n = plans.filter((p) => p.active === false).length; return n ? `(${n})` : ""; })()}
            </label>
            {(() => {
              const visible = plans.filter((p) => showArchived || p.active !== false);
              if (visible.length === 0) return <p className="text-zinc-500">Aucun plan{!showArchived && plans.length ? " actif" : ""}.</p>;
              return visible.map((p) => (
              <div key={p.id} className={`rounded-lg border bg-zinc-900/50 p-3 text-sm flex items-start justify-between gap-3 ${editingPlanId === p.id ? "border-amber-500" : "border-zinc-800"} ${p.active === false ? "opacity-50" : ""}`} data-testid={`plan-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2">{p.name}
                    {p.active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300" data-testid={`plan-archived-${p.id}`}>ARCHIVÉ</span>}</div>
                  <div className="text-zinc-400 text-xs">{p.discipline} · {p.frequency}/sem · {p.age_group} · {p.duration_weeks} sem · {fmt(p.total_price)} · jusqu'à {p.max_installments} versement(s)</div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => togglePlanActive(p)} data-testid={`plan-toggle-${p.id}`}
                    className={`text-xs font-semibold rounded px-2 py-1 border ${p.active === false ? "text-zinc-400 border-zinc-700 hover:border-emerald-700 hover:text-emerald-300" : "text-emerald-400 border-emerald-900/60 hover:border-emerald-700"}`}>{p.active === false ? "Réactiver" : "Archiver"}</button>
                  <button onClick={() => editPlan(p)} data-testid={`plan-edit-${p.id}`}
                    className="text-xs font-semibold text-amber-400 hover:text-amber-300 border border-amber-900/60 hover:border-amber-700 rounded px-2 py-1">✏️ Modifier</button>
                  <button onClick={() => duplicatePlan(p)} data-testid={`plan-duplicate-${p.id}`}
                    className="text-xs font-semibold text-sky-400 hover:text-sky-300 border border-sky-900/60 hover:border-sky-700 rounded px-2 py-1">⧉ Dupliquer</button>
                  <button onClick={() => deletePlan(p)} data-testid={`plan-delete-${p.id}`}
                    className="text-xs font-semibold text-red-400 hover:text-red-300 border border-red-900/60 hover:border-red-700 rounded px-2 py-1">🗑</button>
                </div>
              </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* NOUVEL ABONNEMENT */}
      {!loading && tab === "nouveau" && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 space-y-3 max-w-lg" data-testid="sub-form">
          <button type="button" onClick={() => { setFormerOpen(true); loadFormer(); }}
            className="w-full py-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm font-semibold hover:bg-amber-500/20 transition-colors"
            data-testid="reactivate-former-btn">
            ↩︎ Réactiver un ancien membre (import ZenPlanner)
          </button>
          <input className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Nom du membre" value={subForm.member_name} onChange={(e) => setSubForm({ ...subForm, member_name: e.target.value })} data-testid="sub-name" />
          <input className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Courriel du membre" value={subForm.member_email} onChange={(e) => setSubForm({ ...subForm, member_email: e.target.value })} data-testid="sub-email" />
          <input className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Téléphone (+1...) — pour les relances SMS" value={subForm.member_phone} onChange={(e) => setSubForm({ ...subForm, member_phone: e.target.value })} data-testid="sub-phone" />
          <select className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" value={subForm.plan_id} onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })} data-testid="sub-plan">
            <option value="">— Choisir un plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({fmt(p.total_price)})</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" value={subForm.start_date} onChange={(e) => setSubForm({ ...subForm, start_date: e.target.value })} data-testid="sub-start" />
            <input type="number" min="1" className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Nb versements" value={subForm.num_installments} onChange={(e) => setSubForm({ ...subForm, num_installments: e.target.value })} data-testid="sub-installments" />
            <select className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm col-span-2" value={subForm.payment_method} onChange={(e) => setSubForm({ ...subForm, payment_method: e.target.value })} data-testid="sub-method">
              {[["comptant", "Comptant"], ["carte", "Carte"], ["debit", "Débit"], ["pad", "Prélèvement bancaire"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button onClick={createSub} className="w-full py-2 rounded bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400" data-testid="sub-create-btn">Créer l'abonnement</button>
        </div>
      )}

      {/* VALIDATION ENTRÉE */}
      {!loading && tab === "validation" && (
        <div className="max-w-xl" data-testid="checkin-section">
          <p className="text-zinc-400 text-sm mb-3">Cherche un membre par nom ou courriel pour voir s'il est à jour avant le cours.</p>
          <div className="flex gap-2 mb-4">
            <input className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" placeholder="Nom ou courriel du membre…" value={checkQ}
              onChange={(e) => setCheckQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runCheckIn()} data-testid="checkin-input" />
            <button onClick={runCheckIn} className="px-4 py-2 rounded bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400" data-testid="checkin-btn">Vérifier</button>
          </div>
          {checkRes && (
            <div className="space-y-2" data-testid="checkin-results">
              {checkRes.count === 0 && <p className="text-zinc-500">Aucun membre actif trouvé pour « {checkRes.query} ».</p>}
              {checkRes.results.map((r, i) => (
                <div key={i} className={`rounded-xl border p-4 ${r.access === "ok" ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid={`checkin-result-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-lg">{r.member_name}</div>
                      <div className="text-xs text-zinc-400">{r.member_email} · {r.plan_name}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${r.access === "ok" ? "text-emerald-400" : "text-red-400"}`}>{r.access === "ok" ? "✅ À JOUR" : "⚠️ EN RETARD"}</div>
                      {r.access !== "ok" && <div className="text-sm text-red-300">Doit {fmt(r.amount_owed)}{r.next_due ? ` · depuis ${r.next_due}` : ""}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* RELANCES — suivi relancés / réglés */}
      {!loading && tab === "cartes" && <CardsSyncPanel />}
      {!loading && tab === "regrouper" && <BillingGroupsPanel />}
      {!loading && tab === "sante" && <><StripeHealthPanel /><AuditPanel /></>}
      {!loading && tab === "approuver" && <OutboxPanel />}
      {!loading && tab === "defaut" && <DuesAttendancePanel />}

      {!loading && tab === "relances" && (
        <div className="space-y-4" data-testid="followups-log">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-zinc-700 bg-zinc-900/50 p-4">
              <div className="text-sm text-zinc-400">Relancés</div>
              <div className="text-2xl font-bold">{followupLog?.totals?.reminded ?? 0}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="text-sm text-emerald-300">✅ Réglés</div>
              <div className="text-2xl font-bold">{followupLog?.totals?.resolved ?? 0}</div>
            </div>
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <div className="text-sm text-red-300">🔴 En attente</div>
              <div className="text-2xl font-bold">{followupLog?.totals?.pending ?? 0}</div>
            </div>
          </div>

          {!followupLog || followupLog.rows.length === 0 ? (
            <p className="text-zinc-400 text-sm">Aucun membre relancé pour le moment. La relance automatique quotidienne alimentera ce suivi.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-zinc-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 border-b border-zinc-800 bg-zinc-900/50">
                    <th className="py-2 px-3 font-semibold">Membre</th>
                    <th className="py-2 px-3 font-semibold">Montant</th>
                    <th className="py-2 px-3 font-semibold">Dernière relance</th>
                    <th className="py-2 px-3 font-semibold text-center">Relances</th>
                    <th className="py-2 px-3 font-semibold">Statut</th>
                    <th className="py-2 px-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {followupLog.rows.map((r) => (
                    <tr key={r.contract_id} data-testid={`followup-row-${r.contract_id}`} className="border-b border-zinc-800/60">
                      <td className="py-2 px-3">{r.member_name}<div className="text-xs text-zinc-500">{r.email}</div></td>
                      <td className="py-2 px-3">{r.amount || "—"}</td>
                      <td className="py-2 px-3 text-zinc-400">{r.last_reminded}</td>
                      <td className="py-2 px-3 text-center">{r.reminder_count}</td>
                      <td className="py-2 px-3">
                        {r.status === "paid"
                          ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">✅ Réglé</span>
                          : <span className="text-xs px-2 py-1 rounded-full bg-red-500/15 text-red-300 font-semibold">🔴 En attente</span>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {r.status !== "paid" && (
                          <button onClick={() => markPaid(r.contract_id, r.member_name)} data-testid={`mark-paid-${r.contract_id}`}
                            className="px-3 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 font-semibold">Marquer payé</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* MODALE — Réactiver un ancien membre */}
      {formerOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setFormerOpen(false)} data-testid="former-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg my-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h3 className="font-semibold text-zinc-100">Réactiver un ancien membre</h3>
              <button onClick={() => setFormerOpen(false)} className="text-zinc-400 hover:text-white text-xl leading-none" data-testid="former-close">×</button>
            </div>
            <div className="p-4 space-y-3">
              <input autoFocus className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm"
                placeholder="Chercher par nom ou courriel…" value={formerQ}
                onChange={(e) => { setFormerQ(e.target.value); loadFormer(e.target.value); }}
                data-testid="former-search" />
              <div className="max-h-80 overflow-y-auto divide-y divide-zinc-800" data-testid="former-list">
                {formerLoading ? (
                  <p className="text-zinc-500 text-sm py-6 text-center">Chargement…</p>
                ) : formerList.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-6 text-center">Aucun ancien membre trouvé.</p>
                ) : formerList.map((m) => (
                  <button key={m.id} onClick={() => !m.already_active && pickFormer(m)} disabled={m.already_active}
                    data-testid={`former-row-${m.id}`}
                    className={`w-full text-left py-2.5 px-2 flex items-center justify-between gap-2 ${m.already_active ? "opacity-40 cursor-not-allowed" : "hover:bg-zinc-800/60"}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-100 truncate">{m.full_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{m.email || "— sans courriel —"}{m.phone ? ` · ${m.phone}` : ""}</div>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded shrink-0 ${m.already_active ? "bg-emerald-500/15 text-emerald-400" : m.former_status === "Alumni" ? "bg-amber-500/15 text-amber-400" : "bg-zinc-700/40 text-zinc-300"}`}>
                      {m.already_active ? "Déjà actif" : m.former_status}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500">Sélectionne un membre : son nom, courriel et téléphone seront pré-remplis. Tu choisis ensuite le plan et le mode de paiement.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
